import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SideNavbar from '../components/Navbar/SideNavbar';
import Header from '../components/Header/Header';
import { apis } from '../../core/services/params';
import axiosInstance from '../../core/services/axiosConfig';
import {
    Table,
    Input,
    DatePicker,
    Select,
    Button,
    Tag,
    Spin,
    Empty,
    Space,
    Tabs,
    Tooltip,
    Alert,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    ArrowLeftOutlined,
    ReloadOutlined,
    FilterOutlined,
    WhatsAppOutlined,
    MessageOutlined,
    UserOutlined,
    LikeOutlined,
    ShoppingOutlined,
    FileSearchOutlined,
    SearchOutlined,
    TruckOutlined,
    CustomerServiceOutlined,
    BarChartOutlined,
    SmileOutlined,
    CopyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
    Cell,
} from 'recharts';

const { RangePicker } = DatePicker;

/* ═══════════════════════════════════════════════════════════════
   Types — shape of GET /api/whatsapp-helper/chat-history-summary
   ═══════════════════════════════════════════════════════════════ */
interface FeedbackBreakdownItem { phone_number: string; feedback_values: string[]; }
interface FeedbackBucket { good: number; average: number; poor: number; total_selections?: number; total_users_with_feedback?: number; }
interface FeedbackSummary { breakdown: FeedbackBreakdownItem[]; last: FeedbackBucket; overall: FeedbackBucket; }

interface OrderIdsBreakdownItem { phone_number: string; order_ids: number[]; }
interface OrderIdsSummary { breakdown: OrderIdsBreakdownItem[]; order_ids: number[]; total_unique_order_ids: number; }

interface ProductQueryBreakdownItem { phone_number: string; queries: string[]; }
interface ProductEnquiriesSummary {
    breakdown: ProductQueryBreakdownItem[];
    queries: string[];
    total_customers: number;
    total_unique_queries: number;
}

interface IdsBreakdownItem { phone_number: string; item_ids?: number[]; ticket_ids?: number[]; }
interface ShipmentSummary { breakdown: IdsBreakdownItem[]; item_ids: number[]; total_customers: number; total_unique_item_ids: number; }
interface TicketsSummary { breakdown: IdsBreakdownItem[]; ticket_ids: number[]; total_unique_ticket_ids: number; }

interface WaSummary {
    analyzed_chats: number;
    filters: { end_date: string | null; sender_id: string | null; start_date: string | null };
    summary: {
        feedback: FeedbackSummary;
        order_confirmations: OrderIdsSummary;
        order_enquiries: OrderIdsSummary;
        product_enquiries: ProductEnquiriesSummary;
        shipment_tracking: ShipmentSummary;
        tickets_raised: TicketsSummary;
        total_unique_chats: number;
        total_unique_customers: number;
    };
    success: boolean;
}

/* ═══════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════ */
const formatDateForAPI = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
};

const fmtPhone = (p: string) => (p?.startsWith('+') ? p : `+${p}`);

/** Bucket "N items per customer" distributions used by Shipment / Tickets tabs */
const bucketDistribution = (breakdown: IdsBreakdownItem[], field: 'item_ids' | 'ticket_ids', noun: string) => {
    const buckets = { '1': 0, '2': 0, '3': 0, '4+': 0 };
    breakdown.forEach(row => {
        const n = (row[field] || []).length;
        if (n <= 1) buckets['1']++;
        else if (n === 2) buckets['2']++;
        else if (n === 3) buckets['3']++;
        else buckets['4+']++;
    });
    return Object.entries(buckets).map(([bucket, count]) => ({ bucket: `${bucket} ${noun}${bucket === '1' ? '' : 's'}`, count }));
};

/** Frequency count of each free-text query across all customers */
const topQueries = (breakdown: ProductQueryBreakdownItem[], top = 12) => {
    const freq = new Map<string, number>();
    breakdown.forEach(row => (row.queries || []).forEach(q => {
        const key = q.trim();
        if (!key) return;
        freq.set(key, (freq.get(key) || 0) + 1);
    }));
    return Array.from(freq.entries())
        .map(([query, count]) => ({ query, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, top);
};

/* ═══════════════════════════════════════════════════════════════
   CMS theme tokens — mirrors WhatsAppChatHistoryDashboard.tsx
   ═══════════════════════════════════════════════════════════════ */
const CMS = {
    hero: '#43425d',
    heroDark: '#2b2b40',
    heroLight: '#57567a',
    accent: '#25D366',
    accentDark: '#128C7E',
    border: '#eef0f6',
    bg: '#f4f5f9',
    cardBg: '#ffffff',
    text: '#2d2c3e',
    textMuted: '#8a8aaa',
};

/** Category colour language — reused verbatim from the parent dashboard's row tags,
 *  so a "confirmed order" is always green, an "issue/ticket" always red, etc. */
const HUE = {
    blue: '#2563eb',     // order enquiries
    green: '#16a34a',    // order confirmations / good feedback
    amber: '#d97706',    // average feedback
    red: '#dc2626',      // tickets raised / poor feedback
    purple: '#7c3aed',   // shipment tracking
    orange: '#ea580c',   // product enquiries
    muted: '#94a3b8',    // "last session" comparison series
};

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '20px 24px 32px', background: CMS.bg, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" },
    heroBar: {
        background: `linear-gradient(135deg, ${CMS.heroDark} 0%, ${CMS.hero} 60%, ${CMS.heroLight} 100%)`,
        borderRadius: 16, padding: '20px 28px', marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 12px 30px rgba(67,66,93,0.15)', border: '1px solid rgba(255,255,255,0.08)',
        position: 'relative', overflow: 'hidden',
    },
    heroDecor: { position: 'absolute', right: -40, top: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' },
    heroDecor2: { position: 'absolute', right: 90, bottom: -50, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' },
    filterCard: {
        background: CMS.cardBg, borderRadius: 14, border: `1px solid ${CMS.border}`,
        padding: '18px 24px', marginBottom: 20, boxShadow: '0 10px 30px rgba(67,66,93,0.03), 0 1px 3px rgba(67,66,93,0.02)',
    },
    filterLabel: { fontSize: 10, fontWeight: 700, color: CMS.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, display: 'block' },
    quickChip: {
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 16, padding: '5px 12px',
        fontSize: 11, fontWeight: 600, color: '#64748b', cursor: 'pointer', height: 28, lineHeight: '16px',
        transition: 'all 0.18s ease', whiteSpace: 'nowrap',
    },
    quickChipActive: {
        background: 'linear-gradient(135deg, #43425d 0%, #2b2b40 100%)', borderColor: '#43425d', color: '#fff',
        boxShadow: '0 3px 10px rgba(67,66,93,0.18)',
    },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 20 },
    kpiCard: {
        background: CMS.cardBg, borderRadius: 12, border: `1px solid ${CMS.border}`, padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 6px 18px rgba(67,66,93,0.03), 0 1px 3px rgba(67,66,93,0.02)',
    },
    kpiIconWrap: { width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    kpiLabel: { fontSize: 10, fontWeight: 700, color: CMS.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 },
    kpiValue: { fontSize: 21, fontWeight: 800, lineHeight: 1.1, color: CMS.text },
    chartCard: {
        background: CMS.cardBg, borderRadius: 14, border: `1px solid ${CMS.border}`, padding: '18px 20px 8px',
        boxShadow: '0 10px 30px rgba(67,66,93,0.03)', marginBottom: 16,
    },
    chartTitle: { fontWeight: 700, fontSize: 14, color: CMS.text, marginBottom: 2 },
    chartSub: { fontSize: 12, color: CMS.textMuted, marginBottom: 12 },
    tableCard: { background: CMS.cardBg, borderRadius: 14, border: `1px solid ${CMS.border}`, overflow: 'hidden', boxShadow: '0 10px 30px rgba(67,66,93,0.03)' },
    tableToolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', borderBottom: `1px solid ${CMS.border}`, flexWrap: 'wrap' },
    tagBase: { borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', border: '1px solid transparent' },
    globalLoaderOverlay: {
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(244,245,249,0.6)', backdropFilter: 'blur(1.5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    globalLoaderCard: {
        background: '#fff', borderRadius: 16, padding: '26px 40px',
        boxShadow: '0 24px 60px rgba(67,66,93,0.22)', border: `1px solid ${CMS.border}`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
    },
    globalLoaderText: { fontSize: 13, fontWeight: 700, color: CMS.text },
    globalLoaderSub: { fontSize: 11, color: CMS.textMuted },
};

const tagStyle = (color: string): React.CSSProperties => ({
    ...styles.tagBase,
    color,
    background: `${color}14`,
    borderColor: `${color}33`,
});

/* ═══════════════════════════════════════════════════════════════
   Small reusable pieces
   ═══════════════════════════════════════════════════════════════ */
const KpiTile: React.FC<{ icon: React.ReactNode; label: string; value: number; color: string; bg: string }> = ({ icon, label, value, color, bg }) => (
    <div style={styles.kpiCard}>
        <div style={{ ...styles.kpiIconWrap, background: bg }}>{icon}</div>
        <div>
            <div style={styles.kpiLabel}>{label}</div>
            <div style={{ ...styles.kpiValue, color }}>{(value ?? 0).toLocaleString()}</div>
        </div>
    </div>
);

const SectionHeading: React.FC<{ icon: React.ReactNode; title: string; count?: number }> = ({ icon, title, count }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ color: CMS.hero, fontSize: 15, display: 'flex' }}>{icon}</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: CMS.text, letterSpacing: '-0.1px' }}>{title}</span>
        {typeof count === 'number' && (
            <span style={{ background: '#eeedf5', color: CMS.hero, fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20, border: '1px solid #c8c7df' }}>
                {count.toLocaleString()} total
            </span>
        )}
    </div>
);

const IdTagList: React.FC<{ ids: (string | number)[]; color: string; max?: number }> = ({ ids, color, max = 3 }) => {
    if (!ids?.length) return <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>;
    const shown = ids.slice(0, max);
    const rest = ids.slice(max);
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            {shown.map((id, i) => <span key={i} style={tagStyle(color)}>#{id}</span>)}
            {rest.length > 0 && (
                <Tooltip title={rest.map(r => `#${r}`).join(', ')}>
                    <span style={{ ...tagStyle(color), cursor: 'default' }}>+{rest.length}</span>
                </Tooltip>
            )}
        </div>
    );
};

/** Comma-separated dump of every value in a list, with a one-click copy-to-clipboard button */
const CopyAllBox: React.FC<{ title: string; values: (string | number)[] }> = ({ title, values }) => {
    const text = values.join(', ');
    const handleCopy = async () => {
        if (!values.length) return;
        try {
            await navigator.clipboard.writeText(text);
            message.success(`Copied ${values.length.toLocaleString()} value${values.length === 1 ? '' : 's'} to clipboard`);
        } catch {
            message.error('Failed to copy to clipboard');
        }
    };
    return (
        <div style={styles.chartCard}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                <div>
                    <div style={styles.chartTitle}>{title}</div>
                    <div style={styles.chartSub}>{values.length.toLocaleString()} comma-separated values</div>
                </div>
                <Tooltip title="Copy all to clipboard">
                    <Button icon={<CopyOutlined />} onClick={handleCopy} disabled={!values.length} style={{ borderRadius: 8, flexShrink: 0 }} />
                </Tooltip>
            </div>
            <Input.TextArea
                value={text}
                readOnly
                autoSize={{ minRows: 2, maxRows: 6 }}
                onFocus={e => e.target.select()}
                placeholder="No values for the selected period"
                style={{ borderRadius: 8, fontSize: 12, color: CMS.textMuted, resize: 'none' }}
            />
        </div>
    );
};

const ChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: '#fff', border: `1px solid ${CMS.border}`, borderRadius: 10, padding: '10px 12px', boxShadow: '0 10px 30px rgba(67,66,93,0.12)', fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: CMS.text, marginBottom: 4 }}>{label}</div>
            {payload.map((p: any, i: number) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, color: CMS.textMuted }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || p.fill }} />
                    {p.name}: <b style={{ color: CMS.text }}>{Number(p.value).toLocaleString()}</b>
                </div>
            ))}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════════════════════ */
const WhatsAppDetailedAnalytics: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const initialStart = searchParams.get('start_date') || dayjs().subtract(6, 'day').format('YYYY-MM-DD');
    const initialEnd = searchParams.get('end_date') || dayjs().format('YYYY-MM-DD');

    const [dateRange, setDateRange] = useState<[string, string]>([initialStart, initialEnd]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<WaSummary | null>(null);
    const [activeTab, setActiveTab] = useState('overview');

    /* per-tab search boxes */
    const [feedbackSearch, setFeedbackSearch] = useState('');
    const [feedbackFilter, setFeedbackFilter] = useState<string | undefined>(undefined);
    const [ocSearch, setOcSearch] = useState('');
    const [oeSearch, setOeSearch] = useState('');
    const [peSearch, setPeSearch] = useState('');
    const [shipSearch, setShipSearch] = useState('');
    const [ticketSearch, setTicketSearch] = useState('');

    const fetchAnalytics = async (range: [string, string] = dateRange) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: '1', limit: '10' });
            if (range[0]) params.append('start_date', formatDateForAPI(range[0]));
            if (range[1]) params.append('end_date', formatDateForAPI(range[1]));
            const response = await axiosInstance.get<WaSummary>(`${apis.FETCH_WHATSAPP_SUMMARY}?${params.toString()}`);
            if (response.data?.success) {
                setData(response.data);
            } else {
                setData(null);
                setError('No analytics data found for the selected date range.');
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to fetch detailed analytics.');
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAnalytics(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    /* quick date-range presets — same UX language as the parent dashboard */
    const quickRanges: { label: string; days: number; offset?: number }[] = [
        { label: 'Today', days: 0 },
        { label: 'Yesterday', days: 0, offset: 1 },
        { label: 'Last 7 Days', days: 6 },
        { label: 'Last 30 Days', days: 29 },
        { label: 'Last 90 Days', days: 89 },
    ];
    const isRangeActive = (days: number, offset = 0) => {
        const end = dayjs().subtract(offset, 'day').format('YYYY-MM-DD');
        const start = dayjs().subtract(offset + days, 'day').format('YYYY-MM-DD');
        return dateRange[0] === start && dateRange[1] === end;
    };
    const applyQuickRange = (days: number, offset = 0) => {
        const end = dayjs().subtract(offset, 'day').format('YYYY-MM-DD');
        const start = dayjs().subtract(offset + days, 'day').format('YYYY-MM-DD');
        const range: [string, string] = [start, end];
        setDateRange(range);
        fetchAnalytics(range);
    };
    const handleApply = () => fetchAnalytics(dateRange);
    const handleReset = () => {
        const range: [string, string] = [dayjs().subtract(6, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')];
        setDateRange(range);
        fetchAnalytics(range);
    };

    const summary = data?.summary;

    /* ── derived chart data ─────────────────────────────────────── */
    const feedbackChartData = useMemo(() => {
        if (!summary) return [];
        return [
            { category: 'Good', overall: summary.feedback.overall.good ?? 0, last: summary.feedback.last.good ?? 0 },
            { category: 'Average', overall: summary.feedback.overall.average ?? 0, last: summary.feedback.last.average ?? 0 },
            { category: 'Poor', overall: summary.feedback.overall.poor ?? 0, last: summary.feedback.last.poor ?? 0 },
        ];
    }, [summary]);

    const engagementOverview = useMemo(() => {
        if (!summary) return [];
        const rows = [
            { category: 'Feedback Given', value: summary.feedback.breakdown.length, color: HUE.green },
            { category: 'Order Confirmations', value: summary.order_confirmations.breakdown.length, color: HUE.green },
            { category: 'Order Enquiries', value: summary.order_enquiries.breakdown.length, color: HUE.blue },
            { category: 'Product Enquiries', value: summary.product_enquiries.total_customers, color: HUE.orange },
            { category: 'Shipment Tracking', value: summary.shipment_tracking.total_customers, color: HUE.purple },
            { category: 'Support Tickets', value: summary.tickets_raised.breakdown.length, color: HUE.red },
        ];
        return rows.sort((a, b) => b.value - a.value);
    }, [summary]);

    const orderComparisonData = useMemo(() => {
        if (!summary) return [];
        return [
            { category: 'Order Confirmations', uniqueOrders: summary.order_confirmations.total_unique_order_ids, customers: summary.order_confirmations.breakdown.length },
            { category: 'Order Enquiries', uniqueOrders: summary.order_enquiries.total_unique_order_ids, customers: summary.order_enquiries.breakdown.length },
        ];
    }, [summary]);

    const topQueriesData = useMemo(() => (summary ? topQueries(summary.product_enquiries.breakdown) : []), [summary]);
    const shipmentDistribution = useMemo(() => (summary ? bucketDistribution(summary.shipment_tracking.breakdown, 'item_ids', 'item') : []), [summary]);
    const ticketsDistribution = useMemo(() => (summary ? bucketDistribution(summary.tickets_raised.breakdown, 'ticket_ids', 'ticket') : []), [summary]);

    /* ── filtered table data ────────────────────────────────────── */
    const feedbackRows = useMemo(() => {
        if (!summary) return [];
        let rows = summary.feedback.breakdown;
        if (feedbackFilter) rows = rows.filter(r => r.feedback_values.map(v => v.toUpperCase()).includes(feedbackFilter));
        if (feedbackSearch.trim()) rows = rows.filter(r => r.phone_number.includes(feedbackSearch.trim()));
        return rows;
    }, [summary, feedbackSearch, feedbackFilter]);

    const ocRows = useMemo(() => {
        if (!summary) return [];
        const rows = summary.order_confirmations.breakdown;
        return ocSearch.trim() ? rows.filter(r => r.phone_number.includes(ocSearch.trim()) || r.order_ids.some(id => String(id).includes(ocSearch.trim()))) : rows;
    }, [summary, ocSearch]);

    const oeRows = useMemo(() => {
        if (!summary) return [];
        const rows = summary.order_enquiries.breakdown;
        return oeSearch.trim() ? rows.filter(r => r.phone_number.includes(oeSearch.trim()) || r.order_ids.some(id => String(id).includes(oeSearch.trim()))) : rows;
    }, [summary, oeSearch]);

    const peRows = useMemo(() => {
        if (!summary) return [];
        const rows = summary.product_enquiries.breakdown;
        if (!peSearch.trim()) return rows;
        const q = peSearch.trim().toLowerCase();
        return rows.filter(r => r.phone_number.includes(peSearch.trim()) || r.queries.some(query => query.toLowerCase().includes(q)));
    }, [summary, peSearch]);

    const shipRows = useMemo(() => {
        if (!summary) return [];
        const rows = summary.shipment_tracking.breakdown;
        return shipSearch.trim() ? rows.filter(r => r.phone_number.includes(shipSearch.trim()) || (r.item_ids || []).some(id => String(id).includes(shipSearch.trim()))) : rows;
    }, [summary, shipSearch]);

    const ticketRows = useMemo(() => {
        if (!summary) return [];
        const rows = summary.tickets_raised.breakdown;
        return ticketSearch.trim() ? rows.filter(r => r.phone_number.includes(ticketSearch.trim()) || (r.ticket_ids || []).some(id => String(id).includes(ticketSearch.trim()))) : rows;
    }, [summary, ticketSearch]);

    /* ── table column defs ──────────────────────────────────────── */
    const feedbackColumns: ColumnsType<FeedbackBreakdownItem> = [
        { title: 'Customer', dataIndex: 'phone_number', key: 'phone_number', width: 180, render: (p: string) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: CMS.text }}>
                <WhatsAppOutlined style={{ color: CMS.accent }} />{fmtPhone(p)}
            </span>
        ) },
        { title: 'Feedback Given', key: 'values', render: (_: any, r: FeedbackBreakdownItem) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {r.feedback_values.map((v, i) => {
                    const c = v.toUpperCase() === 'GOOD' ? HUE.green : v.toUpperCase() === 'POOR' ? HUE.red : HUE.amber;
                    return <span key={i} style={tagStyle(c)}>{v}</span>;
                })}
            </div>
        ) },
        { title: 'Responses', key: 'count', width: 110, align: 'center', sorter: (a, b) => a.feedback_values.length - b.feedback_values.length,
            render: (_: any, r: FeedbackBreakdownItem) => <Tag style={{ borderRadius: 20 }}>{r.feedback_values.length}</Tag> },
    ];

    const orderColumns = (color: string): ColumnsType<OrderIdsBreakdownItem> => [
        { title: 'Customer', dataIndex: 'phone_number', key: 'phone_number', width: 180, render: (p: string) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: CMS.text }}>
                <WhatsAppOutlined style={{ color: CMS.accent }} />{fmtPhone(p)}
            </span>
        ) },
        { title: 'Order IDs', key: 'ids', render: (_: any, r: OrderIdsBreakdownItem) => <IdTagList ids={r.order_ids} color={color} /> },
        { title: 'Orders', key: 'count', width: 100, align: 'center', sorter: (a, b) => a.order_ids.length - b.order_ids.length,
            render: (_: any, r: OrderIdsBreakdownItem) => <Tag style={{ borderRadius: 20 }}>{r.order_ids.length}</Tag> },
    ];

    const productColumns: ColumnsType<ProductQueryBreakdownItem> = [
        { title: 'Customer', dataIndex: 'phone_number', key: 'phone_number', width: 180, render: (p: string) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: CMS.text }}>
                <WhatsAppOutlined style={{ color: CMS.accent }} />{fmtPhone(p)}
            </span>
        ) },
        { title: 'Queries', key: 'queries', render: (_: any, r: ProductQueryBreakdownItem) => {
            const shown = r.queries.slice(0, 2);
            const rest = r.queries.slice(2);
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 420 }}>
                    {shown.map((q, i) => <span key={i} style={{ ...tagStyle(HUE.orange), maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q}</span>)}
                    {rest.length > 0 && (
                        <Tooltip title={rest.join(', ')}><span style={tagStyle(HUE.orange)}>+{rest.length}</span></Tooltip>
                    )}
                </div>
            );
        } },
        { title: 'Count', key: 'count', width: 90, align: 'center', sorter: (a, b) => a.queries.length - b.queries.length,
            render: (_: any, r: ProductQueryBreakdownItem) => <Tag style={{ borderRadius: 20 }}>{r.queries.length}</Tag> },
    ];

    const shipmentColumns: ColumnsType<IdsBreakdownItem> = [
        { title: 'Customer', dataIndex: 'phone_number', key: 'phone_number', width: 180, render: (p: string) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: CMS.text }}>
                <WhatsAppOutlined style={{ color: CMS.accent }} />{fmtPhone(p)}
            </span>
        ) },
        { title: 'Item IDs', key: 'ids', render: (_: any, r: IdsBreakdownItem) => <IdTagList ids={r.item_ids || []} color={HUE.purple} /> },
        { title: 'Items', key: 'count', width: 90, align: 'center', sorter: (a, b) => (a.item_ids?.length || 0) - (b.item_ids?.length || 0),
            render: (_: any, r: IdsBreakdownItem) => <Tag style={{ borderRadius: 20 }}>{r.item_ids?.length || 0}</Tag> },
    ];

    const ticketColumns: ColumnsType<IdsBreakdownItem> = [
        { title: 'Customer', dataIndex: 'phone_number', key: 'phone_number', width: 180, render: (p: string) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: CMS.text }}>
                <WhatsAppOutlined style={{ color: CMS.accent }} />{fmtPhone(p)}
            </span>
        ) },
        { title: 'Ticket IDs', key: 'ids', render: (_: any, r: IdsBreakdownItem) => <IdTagList ids={r.ticket_ids || []} color={HUE.red} /> },
        { title: 'Tickets', key: 'count', width: 90, align: 'center', sorter: (a, b) => (a.ticket_ids?.length || 0) - (b.ticket_ids?.length || 0),
            render: (_: any, r: IdsBreakdownItem) => <Tag style={{ borderRadius: 20 }}>{r.ticket_ids?.length || 0}</Tag> },
    ];

    const emptyState = (label: string) => (
        <Empty description={<span style={{ color: '#9ca3af' }}>{label}</span>} style={{ padding: '40px 0' }} />
    );

    /* ── tab contents ───────────────────────────────────────────── */
    const overviewTab = (
        <>
            <div style={styles.chartCard}>
                <div style={styles.chartTitle}>Customer Engagement by Category</div>
                <div style={styles.chartSub}>Unique customers who triggered each conversation type in the selected period</div>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={engagementOverview} layout="vertical" margin={{ top: 4, right: 36, left: 8, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: CMS.textMuted }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                        <YAxis type="category" dataKey="category" width={150} tick={{ fontSize: 12, fill: CMS.text }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                        <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(67,66,93,0.05)' }} />
                        <Bar dataKey="value" name="Customers" radius={[0, 6, 6, 0]} barSize={22}>
                            {engagementOverview.map((row, i) => <Cell key={i} fill={row.color} />)}
                            <LabelList dataKey="value" position="right" style={{ fontSize: 11, fontWeight: 700, fill: CMS.text }} formatter={(v: any) => Number(v).toLocaleString()} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div style={styles.chartCard}>
                <div style={styles.chartTitle}>Feedback Sentiment — Overall vs Last Session</div>
                <div style={styles.chartSub}>Comparison of feedback captured across the whole period vs. only the last chat session</div>
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={feedbackChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }} barGap={6}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" vertical={false} />
                        <XAxis dataKey="category" tick={{ fontSize: 12, fill: CMS.text }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: CMS.textMuted }} axisLine={false} tickLine={false} />
                        <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(67,66,93,0.05)' }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                        <Bar dataKey="overall" name="Overall" fill={CMS.hero} radius={[6, 6, 0, 0]} barSize={28} />
                        <Bar dataKey="last" name="Last Session" fill={HUE.muted} radius={[6, 6, 0, 0]} barSize={28} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </>
    );

    const feedbackTab = (
        <>
            <div style={styles.kpiGrid}>
                <KpiTile icon={<SmileOutlined style={{ color: HUE.green, fontSize: 17 }} />} label="Good (Overall)" value={summary?.feedback.overall.good || 0} color={HUE.green} bg="#f0fdf4" />
                <KpiTile icon={<SmileOutlined style={{ color: HUE.amber, fontSize: 17 }} />} label="Average (Overall)" value={summary?.feedback.overall.average || 0} color={HUE.amber} bg="#fffbeb" />
                <KpiTile icon={<SmileOutlined style={{ color: HUE.red, fontSize: 17 }} />} label="Poor (Overall)" value={summary?.feedback.overall.poor || 0} color={HUE.red} bg="#fef2f2" />
                <KpiTile icon={<LikeOutlined style={{ color: CMS.hero, fontSize: 17 }} />} label="Total Selections" value={summary?.feedback.overall.total_selections || 0} color={CMS.hero} bg="#eeedf5" />
            </div>
            <div style={styles.chartCard}>
                <div style={styles.chartTitle}>Overall vs Last Session</div>
                <div style={styles.chartSub}>Good / Average / Poor feedback volumes</div>
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={feedbackChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }} barGap={6}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" vertical={false} />
                        <XAxis dataKey="category" tick={{ fontSize: 12, fill: CMS.text }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: CMS.textMuted }} axisLine={false} tickLine={false} />
                        <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(67,66,93,0.05)' }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                        <Bar dataKey="overall" name="Overall" fill={CMS.hero} radius={[6, 6, 0, 0]} barSize={28} />
                        <Bar dataKey="last" name="Last Session" fill={HUE.muted} radius={[6, 6, 0, 0]} barSize={28} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div style={styles.tableCard}>
                <div style={styles.tableToolbar}>
                    <SectionHeading icon={<LikeOutlined />} title="Feedback Breakdown by Customer" count={feedbackRows.length} />
                    <Space size={8} wrap>
                        <Select
                            allowClear placeholder="All feedback types" value={feedbackFilter} onChange={v => setFeedbackFilter(v)}
                            style={{ width: 180 }} size="middle"
                            options={[{ value: 'GOOD', label: 'Good' }, { value: 'AVERAGE', label: 'Average' }, { value: 'POOR', label: 'Poor' }]}
                        />
                        <Input.Search allowClear placeholder="Search phone number…" value={feedbackSearch} onChange={e => setFeedbackSearch(e.target.value)} style={{ width: 220 }} />
                    </Space>
                </div>
                <Table<FeedbackBreakdownItem>
                    rowKey="phone_number" columns={feedbackColumns} dataSource={feedbackRows} size="middle"
                    locale={{ emptyText: emptyState('No feedback records found') }}
                    pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`, style: { padding: '14px 20px' } }}
                />
            </div>
        </>
    );

    const ordersTab = (
        <>
            <div style={styles.kpiGrid}>
                <KpiTile icon={<ShoppingOutlined style={{ color: HUE.green, fontSize: 17 }} />} label="Confirmed Orders" value={summary?.order_confirmations.total_unique_order_ids || 0} color={HUE.green} bg="#f0fdf4" />
                <KpiTile icon={<UserOutlined style={{ color: HUE.green, fontSize: 17 }} />} label="Customers Confirming" value={summary?.order_confirmations.breakdown.length || 0} color={HUE.green} bg="#f0fdf4" />
                <KpiTile icon={<FileSearchOutlined style={{ color: HUE.blue, fontSize: 17 }} />} label="Order Enquiries" value={summary?.order_enquiries.total_unique_order_ids || 0} color={HUE.blue} bg="#eff6ff" />
                <KpiTile icon={<UserOutlined style={{ color: HUE.blue, fontSize: 17 }} />} label="Customers Enquiring" value={summary?.order_enquiries.breakdown.length || 0} color={HUE.blue} bg="#eff6ff" />
            </div>
            <div style={styles.chartCard}>
                <div style={styles.chartTitle}>Order Confirmations vs Order Enquiries</div>
                <div style={styles.chartSub}>Unique order IDs and unique customers per conversation type</div>
                <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={orderComparisonData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }} barGap={6}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" vertical={false} />
                        <XAxis dataKey="category" tick={{ fontSize: 12, fill: CMS.text }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: CMS.textMuted }} axisLine={false} tickLine={false} />
                        <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(67,66,93,0.05)' }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                        <Bar dataKey="uniqueOrders" name="Unique Orders" fill={CMS.hero} radius={[6, 6, 0, 0]} barSize={28} />
                        <Bar dataKey="customers" name="Customers" fill={HUE.muted} radius={[6, 6, 0, 0]} barSize={28} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <CopyAllBox title="All Confirmed Order IDs" values={summary?.order_confirmations.order_ids || []} />
            <div style={styles.tableCard}>
                <div style={styles.tableToolbar}>
                    <SectionHeading icon={<ShoppingOutlined />} title="Order Confirmations" count={ocRows.length} />
                    <Input.Search allowClear placeholder="Search phone or order ID…" value={ocSearch} onChange={e => setOcSearch(e.target.value)} style={{ width: 240 }} />
                </div>
                <Table<OrderIdsBreakdownItem>
                    rowKey="phone_number" columns={orderColumns(HUE.green)} dataSource={ocRows} size="middle"
                    locale={{ emptyText: emptyState('No order confirmations found') }}
                    pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`, style: { padding: '14px 20px' } }}
                />
            </div>
            <div style={{ height: 16 }} />
            <CopyAllBox title="All Enquired Order IDs" values={summary?.order_enquiries.order_ids || []} />
            <div style={styles.tableCard}>
                <div style={styles.tableToolbar}>
                    <SectionHeading icon={<FileSearchOutlined />} title="Order Enquiries" count={oeRows.length} />
                    <Input.Search allowClear placeholder="Search phone or order ID…" value={oeSearch} onChange={e => setOeSearch(e.target.value)} style={{ width: 240 }} />
                </div>
                <Table<OrderIdsBreakdownItem>
                    rowKey="phone_number" columns={orderColumns(HUE.blue)} dataSource={oeRows} size="middle"
                    locale={{ emptyText: emptyState('No order enquiries found') }}
                    pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`, style: { padding: '14px 20px' } }}
                />
            </div>
        </>
    );

    const productTab = (
        <>
            <div style={styles.kpiGrid}>
                <KpiTile icon={<SearchOutlined style={{ color: HUE.orange, fontSize: 17 }} />} label="Customers Enquiring" value={summary?.product_enquiries.total_customers || 0} color={HUE.orange} bg="#fff7ed" />
                <KpiTile icon={<BarChartOutlined style={{ color: HUE.orange, fontSize: 17 }} />} label="Unique Queries" value={summary?.product_enquiries.total_unique_queries || 0} color={HUE.orange} bg="#fff7ed" />
            </div>
            <div style={styles.chartCard}>
                <div style={styles.chartTitle}>Top Product Queries</div>
                <div style={styles.chartSub}>Most frequently asked product / order questions, by number of customers</div>
                {topQueriesData.length === 0 ? emptyState('No product queries found') : (
                    <ResponsiveContainer width="100%" height={Math.max(260, topQueriesData.length * 26)}>
                        <BarChart data={topQueriesData} layout="vertical" margin={{ top: 4, right: 36, left: 8, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11, fill: CMS.textMuted }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} allowDecimals={false} />
                            <YAxis type="category" dataKey="query" width={260} tick={{ fontSize: 11, fill: CMS.text }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false}
                                tickFormatter={(v: string) => (v.length > 34 ? `${v.slice(0, 34)}…` : v)} />
                            <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(67,66,93,0.05)' }} />
                            <Bar dataKey="count" name="Customers" fill={HUE.orange} radius={[0, 6, 6, 0]} barSize={16}>
                                <LabelList dataKey="count" position="right" style={{ fontSize: 11, fontWeight: 700, fill: CMS.text }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
            <CopyAllBox title="All Product Search Queries" values={summary?.product_enquiries.queries || []} />
            <div style={styles.tableCard}>
                <div style={styles.tableToolbar}>
                    <SectionHeading icon={<SearchOutlined />} title="Product Enquiries by Customer" count={peRows.length} />
                    <Input.Search allowClear placeholder="Search phone or query text…" value={peSearch} onChange={e => setPeSearch(e.target.value)} style={{ width: 260 }} />
                </div>
                <Table<ProductQueryBreakdownItem>
                    rowKey="phone_number" columns={productColumns} dataSource={peRows} size="middle"
                    locale={{ emptyText: emptyState('No product enquiries found') }}
                    pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`, style: { padding: '14px 20px' } }}
                />
            </div>
        </>
    );

    const shipmentTab = (
        <>
            <div style={styles.kpiGrid}>
                <KpiTile icon={<TruckOutlined style={{ color: HUE.purple, fontSize: 17 }} />} label="Customers Tracking" value={summary?.shipment_tracking.total_customers || 0} color={HUE.purple} bg="#f5f3ff" />
                <KpiTile icon={<BarChartOutlined style={{ color: HUE.purple, fontSize: 17 }} />} label="Unique Items Tracked" value={summary?.shipment_tracking.total_unique_item_ids || 0} color={HUE.purple} bg="#f5f3ff" />
            </div>
            <div style={styles.chartCard}>
                <div style={styles.chartTitle}>Items Tracked per Customer</div>
                <div style={styles.chartSub}>How many shipment items a customer typically asks about in one go</div>
                <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={shipmentDistribution} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" vertical={false} />
                        <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: CMS.text }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: CMS.textMuted }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(67,66,93,0.05)' }} />
                        <Bar dataKey="count" name="Customers" fill={HUE.purple} radius={[6, 6, 0, 0]} barSize={40}>
                            <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 700, fill: CMS.text }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <CopyAllBox title="All Tracked Item IDs" values={summary?.shipment_tracking.item_ids || []} />
            <div style={styles.tableCard}>
                <div style={styles.tableToolbar}>
                    <SectionHeading icon={<TruckOutlined />} title="Shipment Tracking by Customer" count={shipRows.length} />
                    <Input.Search allowClear placeholder="Search phone or item ID…" value={shipSearch} onChange={e => setShipSearch(e.target.value)} style={{ width: 240 }} />
                </div>
                <Table<IdsBreakdownItem>
                    rowKey="phone_number" columns={shipmentColumns} dataSource={shipRows} size="middle"
                    locale={{ emptyText: emptyState('No shipment tracking requests found') }}
                    pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`, style: { padding: '14px 20px' } }}
                />
            </div>
        </>
    );

    const ticketsTab = (
        <>
            <div style={styles.kpiGrid}>
                <KpiTile icon={<CustomerServiceOutlined style={{ color: HUE.red, fontSize: 17 }} />} label="Tickets Raised" value={summary?.tickets_raised.total_unique_ticket_ids || 0} color={HUE.red} bg="#fef2f2" />
                <KpiTile icon={<UserOutlined style={{ color: HUE.red, fontSize: 17 }} />} label="Customers Affected" value={summary?.tickets_raised.breakdown.length || 0} color={HUE.red} bg="#fef2f2" />
            </div>
            <div style={styles.chartCard}>
                <div style={styles.chartTitle}>Tickets Raised per Customer</div>
                <div style={styles.chartSub}>Distribution of how many support tickets each customer raised</div>
                <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={ticketsDistribution} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" vertical={false} />
                        <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: CMS.text }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: CMS.textMuted }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(67,66,93,0.05)' }} />
                        <Bar dataKey="count" name="Customers" fill={HUE.red} radius={[6, 6, 0, 0]} barSize={40}>
                            <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 700, fill: CMS.text }} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <CopyAllBox title="All Ticket IDs" values={summary?.tickets_raised.ticket_ids || []} />
            <div style={styles.tableCard}>
                <div style={styles.tableToolbar}>
                    <SectionHeading icon={<CustomerServiceOutlined />} title="Support Tickets by Customer" count={ticketRows.length} />
                    <Input.Search allowClear placeholder="Search phone or ticket ID…" value={ticketSearch} onChange={e => setTicketSearch(e.target.value)} style={{ width: 240 }} />
                </div>
                <Table<IdsBreakdownItem>
                    rowKey="phone_number" columns={ticketColumns} dataSource={ticketRows} size="middle"
                    locale={{ emptyText: emptyState('No support tickets found') }}
                    pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`, style: { padding: '14px 20px' } }}
                />
            </div>
        </>
    );

    const tabItems = [
        { key: 'overview', label: <span><BarChartOutlined /> Overview</span>, children: overviewTab },
        { key: 'feedback', label: <span><LikeOutlined /> Feedback</span>, children: feedbackTab },
        { key: 'orders', label: <span><ShoppingOutlined /> Orders</span>, children: ordersTab },
        { key: 'product', label: <span><SearchOutlined /> Product Enquiries</span>, children: productTab },
        { key: 'shipment', label: <span><TruckOutlined /> Shipment Tracking</span>, children: shipmentTab },
        { key: 'tickets', label: <span><CustomerServiceOutlined /> Support Tickets</span>, children: ticketsTab },
    ];

    /* ── render ─────────────────────────────────────────────────── */
    return (
        <div className="flex mainWrapper w-full">
            {loading && (
                <div style={styles.globalLoaderOverlay}>
                    <div style={styles.globalLoaderCard}>
                        <Spin size="large" />
                        <div style={styles.globalLoaderText}>Crunching WhatsApp analytics…</div>
                        <div style={styles.globalLoaderSub}>This can take a moment for large date ranges</div>
                    </div>
                </div>
            )}
            <div className="sidenav-wrapper"><SideNavbar /></div>
            <div className="right-container">
                <div className="header-container"><Header /></div>
                <div className="body-container">
                    <div style={styles.page}>

                        {/* ── Hero bar ───────────────────────────────────── */}
                        <div style={styles.heroBar}>
                            <div style={styles.heroDecor} />
                            <div style={styles.heroDecor2} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1 }}>
                                <Button
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => navigate('/whatsapp-chatbot-analytics')}
                                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 10, height: 40, width: 40 }}
                                />
                                <div style={{
                                    width: 48, height: 48, borderRadius: 14, background: CMS.accent,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    boxShadow: '0 3px 12px rgba(37,211,102,0.35)',
                                }}>
                                    <BarChartOutlined style={{ color: '#fff', fontSize: 22 }} />
                                </div>
                                <div>
                                    <h1 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' }}>Detailed Analytics</h1>
                                    <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 400 }}>
                                        Deep-dive into WhatsApp chatbot feedback, orders, enquiries, shipments &amp; tickets
                                    </p>
                                </div>
                            </div>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <Tooltip title="Refresh">
                                    <Button
                                        icon={<ReloadOutlined spin={loading} />}
                                        onClick={() => fetchAnalytics()}
                                        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 8, height: 36 }}
                                    />
                                </Tooltip>
                            </div>
                        </div>

                        {/* ── Filter bar ─────────────────────────────────── */}
                        <div style={styles.filterCard}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
                                <FilterOutlined style={{ color: CMS.hero, fontSize: 13 }} />
                                <span style={{ fontWeight: 700, fontSize: 13, color: CMS.text }}>Filters</span>
                                {data?.filters?.start_date && (
                                    <span style={{ fontSize: 11, color: CMS.textMuted, marginLeft: 4 }}>
                                        Showing {data.filters.start_date} → {data.filters.end_date}
                                    </span>
                                )}
                            </div>
                            <Space size={16} wrap align="end">
                                <div>
                                    <span style={styles.filterLabel}>Date Range</span>
                                    <RangePicker
                                        value={[dayjs(dateRange[0], 'YYYY-MM-DD'), dayjs(dateRange[1], 'YYYY-MM-DD')]}
                                        onChange={vals => { if (vals && vals[0] && vals[1]) setDateRange([vals[0].format('YYYY-MM-DD'), vals[1].format('YYYY-MM-DD')]); }}
                                        disabledDate={d => !!d && d.isAfter(dayjs(), 'day')}
                                        format="DD/MM/YYYY"
                                        allowClear={false}
                                        style={{ borderRadius: 8, height: 36 }}
                                    />
                                </div>
                                <div>
                                    <span style={styles.filterLabel}>Quick Range</span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {quickRanges.map(({ label, days, offset }) => {
                                            const active = isRangeActive(days, offset);
                                            return (
                                                <button
                                                    key={label} type="button"
                                                    onClick={() => applyQuickRange(days, offset)}
                                                    style={{ ...styles.quickChip, ...(active ? styles.quickChipActive : {}) }}
                                                    className={active ? 'da-quick-chip da-quick-chip-active' : 'da-quick-chip'}
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <Space size={8}>
                                    <Button
                                        type="primary" icon={<FilterOutlined />} loading={loading} onClick={handleApply}
                                        style={{ borderRadius: 8, background: 'linear-gradient(135deg, #43425d 0%, #2b2b40 100%)', borderColor: '#43425d', fontWeight: 600, height: 36, boxShadow: '0 4px 12px rgba(67, 66, 93, 0.15)' }}
                                        className="da-apply-btn"
                                    >
                                        Apply
                                    </Button>
                                    <Tooltip title="Reset to last 7 days">
                                        <Button icon={<ReloadOutlined />} onClick={handleReset} style={{ borderRadius: 8, height: 36, border: '1px solid #e2e8f0', color: '#64748b' }} />
                                    </Tooltip>
                                </Space>
                            </Space>
                        </div>

                        {error && <Alert type="error" message={error} closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 10 }} />}

                        <>
                            {/* ── Top-level KPI row ─────────────────────── */}
                            <div style={styles.kpiGrid}>
                                <KpiTile icon={<MessageOutlined style={{ color: CMS.hero, fontSize: 17 }} />} label="Total Unique Chats" value={summary?.total_unique_chats || 0} color={CMS.hero} bg="#eeedf5" />
                                <KpiTile icon={<UserOutlined style={{ color: CMS.hero, fontSize: 17 }} />} label="Total Unique Customers" value={summary?.total_unique_customers || 0} color={CMS.hero} bg="#eeedf5" />
                                <KpiTile icon={<WhatsAppOutlined style={{ color: CMS.accentDark, fontSize: 17 }} />} label="Analyzed Chats" value={data?.analyzed_chats || 0} color={CMS.accentDark} bg="#f0fdf4" />
                                <KpiTile icon={<ShoppingOutlined style={{ color: HUE.green, fontSize: 17 }} />} label="Confirmed Orders" value={summary?.order_confirmations.total_unique_order_ids || 0} color={HUE.green} bg="#f0fdf4" />
                                <KpiTile icon={<FileSearchOutlined style={{ color: HUE.blue, fontSize: 17 }} />} label="Order Enquiries" value={summary?.order_enquiries.total_unique_order_ids || 0} color={HUE.blue} bg="#eff6ff" />
                                <KpiTile icon={<TruckOutlined style={{ color: HUE.purple, fontSize: 17 }} />} label="Items Tracked" value={summary?.shipment_tracking.total_unique_item_ids || 0} color={HUE.purple} bg="#f5f3ff" />
                                <KpiTile icon={<CustomerServiceOutlined style={{ color: HUE.red, fontSize: 17 }} />} label="Support Tickets" value={summary?.tickets_raised.total_unique_ticket_ids || 0} color={HUE.red} bg="#fef2f2" />
                                <KpiTile icon={<SearchOutlined style={{ color: HUE.orange, fontSize: 17 }} />} label="Product Enquiries" value={summary?.product_enquiries.total_customers || 0} color={HUE.orange} bg="#fff7ed" />
                            </div>

                            {/* ── Tabbed detail sections ─────────────────── */}
                            {summary ? (
                                <Tabs
                                    activeKey={activeTab}
                                    onChange={setActiveTab}
                                    items={tabItems}
                                    className="da-tabs"
                                />
                            ) : !loading && (
                                <div style={styles.tableCard}>{emptyState('No analytics data available for the selected date range')}</div>
                            )}
                        </>

                    </div>
                </div>
            </div>

            <style>{`
                .ant-table-thead > tr > th {
                    background: #f8fafc !important;
                    font-weight: 700 !important;
                    font-size: 11px !important;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #64748b !important;
                    border-bottom: 1px solid #e2e8f0 !important;
                    padding: 14px 16px !important;
                }
                .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f1f5f9 !important;
                    padding: 12px 16px !important;
                    font-size: 13px !important;
                    color: #334155 !important;
                }
                .ant-table-tbody > tr:hover > td { background: #f8fafc !important; }

                .ant-input-affix-wrapper, .ant-picker, .ant-select-selector {
                    border-radius: 8px !important;
                    border: 1px solid #e2e8f0 !important;
                    box-shadow: none !important;
                    transition: all 0.2s ease !important;
                }
                .ant-input-affix-wrapper:hover, .ant-picker:hover, .ant-select-selector:hover { border-color: #43425d !important; }
                .ant-input-affix-wrapper-focused, .ant-picker-focused, .ant-select-focused .ant-select-selector {
                    border-color: #43425d !important;
                    box-shadow: 0 0 0 2px rgba(67, 66, 93, 0.12) !important;
                }

                .ant-pagination-item-active { border-color: #43425d !important; background: #43425d !important; }
                .ant-pagination-item-active a { color: #ffffff !important; }
                .ant-pagination-item:hover, .ant-pagination-next:hover, .ant-pagination-prev:hover { border-color: #43425d !important; }
                .ant-pagination-item:hover a, .ant-pagination-next:hover a, .ant-pagination-prev:hover a { color: #43425d !important; }

                .da-quick-chip:not(.da-quick-chip-active):hover { border-color: #43425d !important; color: #43425d !important; background: #eeedf5 !important; }
                .da-quick-chip-active:hover { transform: translateY(-1px); box-shadow: 0 5px 14px rgba(67,66,93,0.25) !important; }
                .da-apply-btn:hover { transform: translateY(-1px); opacity: 0.95; box-shadow: 0 6px 16px rgba(67, 66, 93, 0.22) !important; }

                .da-tabs .ant-tabs-nav { margin-bottom: 16px !important; }
                .da-tabs .ant-tabs-tab { font-weight: 600 !important; font-size: 13px !important; color: #64748b !important; padding: 10px 4px !important; }
                .da-tabs .ant-tabs-tab:hover { color: #43425d !important; }
                .da-tabs .ant-tabs-tab-active .ant-tabs-tab-btn { color: #43425d !important; font-weight: 700 !important; }
                .da-tabs .ant-tabs-ink-bar { background: #43425d !important; height: 3px !important; border-radius: 3px !important; }
            `}</style>
        </div>
    );
};

export default WhatsAppDetailedAnalytics;
